import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $deleteTableColumn__EXPERIMENTAL,
  $deleteTableRow__EXPERIMENTAL,
  $getTableNodeFromLexicalNodeOrThrow,
  $insertTableColumn__EXPERIMENTAL,
  $insertTableRow__EXPERIMENTAL,
  getTableObserverFromTableElement,
  HTMLTableElementWithWithTableSelectionState,
  TableCellNode,
} from '@lexical/table'
import { $getRoot } from 'lexical'
import React, { useCallback, useEffect, useState, FC, useMemo } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Box, Divider, MenuItem } from '@mui/material'

import { useCustomTranslation } from '../../../../Hooks/useCustomTranslation'
import { TABLE_MAX_COLUMNS, TABLE_MAX_ROWS, TABLE_MIN_COLUMNS, TABLE_MIN_ROWS } from '../../const/validation'

const useStyles = makeStyles()(theme => ({
  mainContainer: {
    background: theme.shape.markdownEditor.background,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    boxShadow: theme.shape.markdownEditor.shadowPopover,
    borderRadius: theme.shape.borderRadius,
  },
}))

type TableCellActionMenuProps = Readonly<{
  onClose: () => void
  tableCellNode: TableCellNode
}>

/**
 * Controls for menu popover to actions on existing tables
 */
export const PopoverContent: FC<TableCellActionMenuProps> = ({ onClose, tableCellNode: _tableCellNode }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const [editor] = useLexicalComposerContext()
  const [tableCellNode, updateTableCellNode] = useState(_tableCellNode)

  const { columns, rows } = useMemo(() => {
    let rows = 0
    let columns = 0

    editor.getEditorState().read(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode)
      const tableElement = editor.getElementByKey(tableNode.getKey()) as HTMLTableElement

      rows = tableElement.rows.length
      columns = tableElement.rows[0].cells.length
    })

    return { rows, columns }
  }, [editor, tableCellNode])

  const rowCanBeAdded = rows < TABLE_MAX_ROWS
  const columnCanBeAdded = columns < TABLE_MAX_COLUMNS
  const rowCanBeDeleted = rows > TABLE_MIN_ROWS
  const columnsCanBeDeleted = columns > TABLE_MIN_COLUMNS

  useEffect(() => {
    return editor.registerMutationListener(TableCellNode, nodeMutations => {
      const nodeUpdated = nodeMutations.get(tableCellNode.getKey()) === 'updated'

      if (nodeUpdated) {
        editor.getEditorState().read(() => {
          updateTableCellNode(tableCellNode.getLatest())
        })
      }
    })
  }, [editor, tableCellNode])

  const clearTableSelection = useCallback(() => {
    editor.update(() => {
      if (tableCellNode.isAttached()) {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode)
        const tableElement = editor.getElementByKey(tableNode.getKey()) as HTMLTableElementWithWithTableSelectionState

        if (!tableElement) {
          throw new Error('Expected to find tableElement in DOM')
        }

        const tableSelection = getTableObserverFromTableElement(tableElement)
        if (tableSelection !== null) {
          tableSelection.clearHighlight()
        }

        tableNode.markDirty()
        updateTableCellNode(tableCellNode.getLatest())
      }

      const rootNode = $getRoot()
      rootNode.selectStart()
    })
  }, [editor, tableCellNode])

  const insertTableRowAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        $insertTableRow__EXPERIMENTAL(shouldInsertAfter)
        onClose()
      })
    },
    [editor, onClose]
  )

  const insertTableColumnAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        $insertTableColumn__EXPERIMENTAL(shouldInsertAfter)

        onClose()
      })
    },
    [editor, onClose]
  )

  const deleteTableRowAtSelection = useCallback(() => {
    editor.update(() => {
      $deleteTableRow__EXPERIMENTAL()
      onClose()
    })
  }, [editor, onClose])

  const deleteTableAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode)
      tableNode.remove()

      clearTableSelection()
      onClose()
    })
  }, [editor, tableCellNode, clearTableSelection, onClose])

  const deleteTableColumnAtSelection = useCallback(() => {
    editor.update(() => {
      $deleteTableColumn__EXPERIMENTAL()
      onClose()
    })
  }, [editor, onClose])

  return (
    <Box className={classes.mainContainer}>
      {rowCanBeAdded && (
        <>
          <MenuItem onClick={() => insertTableRowAtSelection(false)}>{t('shell.markdown-editor.insert-row-above')}</MenuItem>
          <MenuItem onClick={() => insertTableRowAtSelection(true)}>{t('shell.markdown-editor.insert-row-below')}</MenuItem>
          <Divider />
        </>
      )}
      {columnCanBeAdded && (
        <>
          <MenuItem onClick={() => insertTableColumnAtSelection(false)}>{t('shell.markdown-editor.insert-column-left')}</MenuItem>
          <MenuItem onClick={() => insertTableColumnAtSelection(true)}>{t('shell.markdown-editor.insert-column-right')}</MenuItem>
          <Divider />
        </>
      )}
      {columnsCanBeDeleted && <MenuItem onClick={deleteTableColumnAtSelection}>{t('shell.markdown-editor.delete-column')}</MenuItem>}
      {rowCanBeDeleted && <MenuItem onClick={deleteTableRowAtSelection}>{t('shell.markdown-editor.delete-row')}</MenuItem>}
      <MenuItem onClick={deleteTableAtSelection}>{t('shell.markdown-editor.delete-table')}</MenuItem>
    </Box>
  )
}
